import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  House, 
  Files, 
  Receipt, 
  Package, 
  Gear,
  SignOut,
  List,
  X,
  ShieldCheck,
  CreditCard,
  Calculator,
  Users,
  ChartLine
} from '@phosphor-icons/react';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    checkAdmin();
    checkSubscription();
  }, []);

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API}/admin/check`);
      setIsAdmin(res.data.is_admin);
    } catch {
      setIsAdmin(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(res.data);
    } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const isSubscriptionActive = subscriptionStatus?.subscription_status === 'active' || isAdmin;

  // Bottom nav items - 5 items max
  const bottomNavItems = [
    { path: '/', icon: House, label: 'Accueil' },
    { path: '/quotes', icon: Files, label: 'Devis' },
    { path: '/invoices', icon: Receipt, label: 'Factures' },
    { path: '/stock', icon: Package, label: 'Stock' },
    { path: '/settings', icon: Gear, label: 'Plus' },
  ];

  // Sidebar menu items
  const menuItems = [
    { path: '/', icon: House, label: 'Tableau de bord', requiresSubscription: true },
    { path: '/requests', icon: Files, label: 'Demandes', requiresSubscription: true },
    { path: '/quotes', icon: Files, label: 'Devis', requiresSubscription: true },
    { path: '/invoices', icon: Receipt, label: 'Factures', requiresSubscription: true },
    { path: '/clients', icon: Users, label: 'Clients', requiresSubscription: true },
    { path: '/stock', icon: Package, label: 'Stock', requiresSubscription: true },
    { path: '/compta', icon: Calculator, label: 'Comptabilité', requiresSubscription: true },
    { path: '/statistics', icon: ChartLine, label: 'Statistiques', requiresSubscription: true },
    { path: '/settings', icon: Gear, label: 'Paramètres', requiresSubscription: false },
    { path: '/subscription', icon: CreditCard, label: 'Abonnement', requiresSubscription: false },
  ].filter(item => isSubscriptionActive || !item.requiresSubscription);

  if (isAdmin) {
    menuItems.push({ path: '/admin', icon: ShieldCheck, label: 'Admin', requiresSubscription: false });
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />
      
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-72 flex-col bg-card border-r border-border z-40">
        {/* Logo */}
        <div className="p-6">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-2 flex items-center justify-center">
              <img 
                src="/angela-logo-clean.png" 
                alt="Angela Devis" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="font-bold text-lg text-foreground">Angela</h1>
              <p className="text-xs text-muted-foreground">Gestion BTP</p>
            </div>
          </div>
        </div>

        {/* User */}
        {user.company_name && (
          <div className="px-6 pb-6">
            <div className="p-4 rounded-2xl bg-muted/50">
              <p className="text-sm font-medium text-foreground truncate">{user.company_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200
                  ${isActive
                    ? 'bg-black dark:bg-white text-white dark:text-black font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
              >
                <Icon size={22} weight={isActive ? 'fill' : 'regular'} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 transition-colors"
          >
            <SignOut size={22} />
            <span className="text-sm">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 px-4 py-3 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 p-1.5 flex items-center justify-center">
              <img 
                src="/angela-logo-clean.png" 
                alt="Angela Devis" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-bold text-foreground">Angela</span>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-muted transition-colors"
            >
              <List size={22} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="lg:hidden fixed right-0 top-0 bottom-0 w-80 bg-card z-50 overflow-y-auto"
            >
              <div className="p-4 flex items-center justify-between border-b border-border">
                <h2 className="font-semibold">Menu</h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-muted"
                >
                  <X size={24} />
                </button>
              </div>
              
              <nav className="p-4 space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-2xl transition-all
                        ${isActive
                          ? 'bg-black dark:bg-white text-white dark:text-black'
                          : 'text-muted-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon size={22} weight={isActive ? 'fill' : 'regular'} />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="p-4 border-t border-border">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  <SignOut size={22} />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen">
        <div className="pt-16 pb-28 lg:pt-0 lg:pb-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </div>
      </main>

      {/* Floating Bottom Nav - Mobile Only */}
      <nav className="floating-nav lg:hidden">
        <div className="flex items-center gap-1">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path === '/settings' && ['/settings', '/compta', '/statistics', '/subscription', '/admin', '/clients', '/requests'].includes(location.pathname));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'nav-item-active' : ''}`}
              >
                <Icon 
                  size={24} 
                  weight={isActive ? 'fill' : 'regular'}
                  className={isActive ? '' : 'text-muted-foreground'}
                />
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
